import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Shield, Camera, User, CreditCard, Server, Lock, Share2, Clock, Ban, Users, FileText, Mail, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

// Animated Section Component
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Policy Section Component
const PolicySection = ({ icon: Icon, title, children, testId }) => {
  return (
    <AnimatedSection className="mb-12">
      <div className="flex items-start gap-4" data-testid={testId}>
        <div className="flex-shrink-0 w-12 h-12 bg-[#F3F4F6] flex items-center justify-center">
          <Icon className="w-6 h-6 text-[#0055FF]" />
        </div>
        <div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-[#121212] mb-4">
            {title}
          </h2>
          <div className="text-gray-600 leading-relaxed font-light space-y-3">
            {children}
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Header Component
const Header = () => {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <a href="/" className="flex items-center gap-2" data-testid="header-logo">
            <span className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-[#121212]">
              LabrHood<span className="text-[#0055FF]">Labs</span>
            </span>
          </a>
          <Button 
            asChild
            variant="ghost"
            className="text-sm font-medium"
            data-testid="back-home-btn"
          >
            <a href="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </a>
          </Button>
        </div>
      </div>
    </header>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="py-12 bg-white border-t border-gray-100" data-testid="footer-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex flex-col items-center justify-center text-center">
          <span className="font-heading text-lg font-bold tracking-tight text-[#121212] mb-4">
            LabrHood<span className="text-[#0055FF]">Labs</span>
          </span>
          <p className="text-sm text-gray-500">
            &copy; 2026 LabrHood Labs
          </p>
        </div>
      </div>
    </footer>
  );
};

export default function InkNMotionPrivacy() {
  const lastUpdated = "June 3, 2026";

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6 sm:px-12">
          {/* Hero */}
          <AnimatedSection className="mb-16">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-gray-500 mb-4 block">
              Legal
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#121212] mb-6" data-testid="privacy-headline">
              Privacy Policy — Ink-N-Motion
            </h1>
            <p className="text-sm text-gray-500">
              Last Updated: {lastUpdated}
            </p>
          </AnimatedSection>

          {/* Introduction */}
          <AnimatedSection className="mb-12">
            <p className="text-gray-600 leading-relaxed font-light">
              This Privacy Policy explains how Ink-N-Motion ("we," "us," the "App"), a product of LabrHood Labs, collects, uses, and shares information when you use our mobile application. By using Ink-N-Motion, you agree to the practices described below.
            </p>
          </AnimatedSection>

          <div className="thin-divider mb-12" />

          {/* Information We Collect */}
          <AnimatedSection className="mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-[#121212] mb-8">
              Information We Collect
            </h2>
          </AnimatedSection>

          <PolicySection 
            icon={User} 
            title="Account Information" 
            testId="policy-account"
          >
            <p>
              You can use Ink-N-Motion as a guest. If you choose to sign in, we use Google Sign-In or Apple Sign-In, which provide us with a user identifier and, depending on your settings, your name, email address, and profile photo. We also maintain an account identifier and your in-app token (credit) balance.
            </p>
          </PolicySection>

          <PolicySection 
            icon={Camera} 
            title="Photos and Prompts" 
            testId="policy-photos"
          >
            <p>
              When you use features such as Animate My Ink, the Cover-Up Designer, or concept tools, the tattoo photos you select and any text prompts you enter are uploaded to our processing service and used to generate your results.
            </p>
          </PolicySection>

          <PolicySection 
            icon={FileText} 
            title="Generated Media" 
            testId="policy-generated"
          >
            <p>
              Images and videos created by the App are produced to deliver your results and may be processed transiently by our AI providers.
            </p>
          </PolicySection>

          <PolicySection 
            icon={CreditCard} 
            title="Purchase Information" 
            testId="policy-purchases"
          >
            <p>
              When you buy token packs or a membership, your purchase and subscription status is processed through Google Play and our subscription manager. We do not receive or store your full payment card details.
            </p>
          </PolicySection>

          <PolicySection 
            icon={Server} 
            title="Technical Information" 
            testId="policy-technical"
          >
            <p>
              Like most apps, we and our service providers may process limited network information (such as IP address) necessary to operate and secure the service.
            </p>
          </PolicySection>

          <div className="thin-divider mb-12" />

          {/* How We Use Information */}
          <PolicySection 
            icon={Shield} 
            title="How We Use Information" 
            testId="policy-use"
          >
            <p>
              We use information to operate the App's creative features, generate and deliver your results, maintain your token balance and account, process purchases and memberships, provide support, and protect against fraud and abuse.
            </p>
          </PolicySection>

          {/* How We Share Information */}
          <PolicySection 
            icon={Share2} 
            title="How We Share Information" 
            testId="policy-share"
          >
            <p>
              We share information only as needed to run the App, with service providers including Google (Firebase, Google Play, Google Sign-In), Apple, our subscription manager (RevenueCat), and AI processing providers (including OpenAI and Kling AI) that generate your images and videos. <strong>We do not sell your personal information.</strong>
            </p>
          </PolicySection>

          {/* Data Security */}
          <PolicySection 
            icon={Lock} 
            title="Data Security" 
            testId="policy-security"
          >
            <p>
              Data transmitted between the App and our services is encrypted in transit using industry-standard protocols.
            </p>
          </PolicySection>

          {/* Data Retention */}
          <PolicySection 
            icon={Clock} 
            title="Data Retention and Your Choices" 
            testId="policy-retention"
          >
            <p>
              You may request access to, or deletion of, your account and associated data by contacting us at the email below. We will respond within a reasonable period and remove data we are not required to retain.
            </p>
          </PolicySection>

          {/* Advertising */}
          <PolicySection 
            icon={Ban} 
            title="Advertising" 
            testId="policy-advertising"
          >
            <p>
              Ink-N-Motion does not contain third-party advertising and does not collect an advertising identifier.
            </p>
          </PolicySection>

          {/* Children */}
          <PolicySection 
            icon={Users} 
            title="Children" 
            testId="policy-children"
          >
            <p>
              Ink-N-Motion is intended for users aged 18 and older. We do not knowingly collect information from anyone under 18.
            </p>
          </PolicySection>

          {/* Changes */}
          <AnimatedSection className="mb-12">
            <h3 className="font-heading text-xl font-bold tracking-tight text-[#121212] mb-4">
              Changes to This Policy
            </h3>
            <p className="text-gray-600 leading-relaxed font-light">
              We may update this Privacy Policy from time to time. Material changes will be reflected by updating the date above.
            </p>
          </AnimatedSection>

          <div className="thin-divider mb-12" />

          {/* Contact Section */}
          <AnimatedSection>
            <div className="bg-[#F9FAFB] p-8 sm:p-12">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-white flex items-center justify-center">
                  <Mail className="w-6 h-6 text-[#0055FF]" />
                </div>
                <div>
                  <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-[#121212] mb-4">
                    Contact Us
                  </h2>
                  <p className="text-gray-600 leading-relaxed font-light mb-4">
                    For privacy questions or data deletion requests, contact:
                  </p>
                  <a 
                    href="mailto:sravenscroft80@gmail.com" 
                    className="text-[#0055FF] hover:underline font-medium"
                    data-testid="contact-email"
                  >
                    sravenscroft80@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </main>

      <Footer />
    </div>
  );
}
